/**
 * Free gift with purchase — auto-add, and back out again if Shopify disagrees.
 *
 * Eligibility maths lives in snippets/cart-gift-unlock.liquid and is recomputed
 * server-side on every cart render, so this file never decides who qualifies.
 * It moves the line in and out:
 *
 *   unlocked -> add the gift automatically (no click needed)
 *   rejected -> the gift is in the cart but Shopify did NOT make it free,
 *               so pull it straight back out and stop trying
 *
 * That second rule is the important one. Shopify's discount-combination rules
 * decide whether the "Buy X get Y" actually applies, and a competing code can
 * beat it. Predicting that from the theme is how a customer ends up billed full price
 * for a gift, so we let Shopify answer and react to it instead.
 *
 * Once a customer removes the gift themselves we stop re-adding it — putting it
 * back on every render would be a fight they cannot win.
 */
(function () {
  // Everything a customer could use to mutate or leave the cart while the gift
  // line is going in. The footer matters as much as the items: it carries the
  // checkout and express-checkout buttons, and letting someone reach checkout
  // mid-flight is how they end up paying for a "free" gift.
  const lockTargets = () => {
    const els = [];
    const items = document.getElementById('main-cart-items');
    if (items) els.push(items);
    // The footer freeze exists for the MONEY controls -- checkout and the
    // express wallets -- not the whole summary. The coupon box stays usable:
    // a code applied mid-flight is safe (every render comes from server truth,
    // and a code that unfunds the gift is exactly what the reject path
    // handles), whereas checkout inside the window is how a customer pays for
    // something the page called free. Fall back to the whole footer only if
    // the CTA containers are not found.
    const footer = document.getElementById('main-cart-footer');
    if (footer) {
      const ctas = footer.querySelectorAll('.cart__ctas, .cart__dynamic-checkout-buttons');
      if (ctas.length) ctas.forEach((el) => els.push(el));
      else els.push(footer);
    }
    // The drawer has the same two surfaces. It matters more since the reorder
    // (see reinsert) empties the cart for one round trip -- a checkout press
    // inside that window would open a checkout for nothing, or for the
    // earrings alone.
    ['CartDrawer-CartItems'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) els.push(el);
    });
    document.querySelectorAll('cart-drawer .cart__ctas, cart-drawer .cart__dynamic-checkout-buttons').forEach((el) => els.push(el));
    return els;
  };

  const KEY_ADDED = 'bcGiftAdded';
  const KEY_BLOCKED = 'bcGiftBlocked'; // 'removed' (customer) | 'refused' (Shopify)
  // Lines taken out of the cart by a reorder (see reorderBehindGift) that have
  // not been put back yet. Cleared the moment the re-add lands; anything still
  // here on the next load is restored before the module does anything else.
  const KEY_REORDER = 'bcGiftReorder';
  // One reorder at a time across every instance on the page (drawer AND page
  // modules can coexist): two racing remove/re-add cycles would duplicate lines.
  let reordering = false;

  // Every state below is a decision NOT to grant the gift. sessionStorage records
  // it for this browser, which is exactly the wrong place for it: the cart
  // transform runs server-side on every checkout — including express and wallet
  // checkouts that never load this page — and would cheerfully hand out at
  // checkout the gift this module just decided to withhold. The customer who
  // deliberately removed it would find it back and unremovable; the customer
  // keeping a competing discount code would get the gift AND the code.
  //
  // So the decision has to live where the server can see it: a `_free_gift`
  // property on a line that is staying in the cart. The transform stands down on
  // ANY non-empty marker, and the cart templates test `== 'true'`, so a
  // 'declined' value is invisible to the UI and decisive at checkout.
  const DECLINED = 'declined';

  // Ceiling on how long the cart may stay shut while the refusal is written. Long
  // enough that a normal round trip always wins the race on a slow connection,
  // short enough that a shopper never wonders whether the page is broken.
  const MARK_TIMEOUT_MS = 4000;

  /**
   * Bound a promise that has no timeout of its own.
   *
   * A RACE AGAINST A REJECTING TIMER, not AbortController. `fetch` that never
   * settles is exactly the case an abort signal does not help with here: the
   * express module's post-mortem was a request that hung with the signal never
   * firing, leaving the caller's `await` parked forever. Racing does not cancel
   * the request — it cannot — but it guarantees the CALLER moves on, which is the
   * only thing the cart needs. The loser settles later into nothing.
   */
  function withTimeout(promise, ms) {
    let timer;
    return Promise.race([
      promise,
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('timeout')), ms);
      }),
    ]).finally(() => clearTimeout(timer));
  }

  const store = {
    get(k) {
      try {
        return sessionStorage.getItem(k);
      } catch (e) {
        return null;
      }
    },
    set(k, v) {
      try {
        sessionStorage.setItem(k, v);
      } catch (e) {
        /* private mode — auto-add still works, it just forgets between loads */
      }
    },
    clear(k) {
      try {
        sessionStorage.removeItem(k);
      } catch (e) {
        /* no-op */
      }
    },
  };

  class CartGiftUnlock extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
    }

    connectedCallback() {
      // Both the standard offer and the reclaim banner carry an add button.
      const buttons = Array.from(this.querySelectorAll('[data-gift-add]'));
      buttons.forEach((b) => b.addEventListener('click', () => this.addGift(b)));
      const button = buttons[0];

      // A relocated banner outlives its module, so any state that is not the
      // reclaim offer must sweep it away -- otherwise an emptied or re-rendered
      // cart keeps showing a claim button for an offer that is gone.
      if (this.dataset.state !== 'unlocked') this.clearSlots();

      // A reorder that removed lines and never got them back leaves its list
      // here. Nothing else may run until they are in the cart again: every
      // state below reasons about a cart that is missing them.
      const pending = store.get(KEY_REORDER);
      if (pending) {
        this.restoreLines(pending).then((restored) => {
          if (!restored) this.start(button);
        });
        return;
      }
      this.start(button);
    }

    start(button) {
      switch (this.dataset.state) {
        case 'added':
          // Healthy: remember it landed, and forgive any earlier refusal. The
          // server-side marker is lifted with the sessionStorage block, so the two
          // records of "we stood down" can never disagree.
          store.set(KEY_ADDED, '1');
          store.clear(KEY_BLOCKED);
          // The reorder (settleOrder) is OFF since 2026-09-30: every re-add fired
          // a real add_to_cart to Meta/GA4 on each cart view, so line order is
          // left as Shopify has it until a side-effect-free reorder exists.
          this.setDeclined(false);
          break;

        case 'locked':
          // Below the threshold — a clean slate for the next time they cross it.
          store.clear(KEY_ADDED);
          store.clear(KEY_BLOCKED);
          this.setDeclined(false);
          break;

        case 'rejected':
          this.reject();
          break;

        case 'unlocked':
          this.maybeAutoAdd(button);
          break;

        default:
          break;
      }
    }

    /**
     * Write, or clear, the server-visible stand-down marker.
     *
     * PROPERTY SURGERY, so it is written defensively. /cart/change.js REPLACES a
     * line's whole properties hash, so the existing properties are read back from
     * /cart.js and copied forward key for key — dropping one would destroy a ring
     * size, an engraving, or the `_expected_price` the transform prices from.
     * Nothing else about the line is sent, so quantity and variant are untouched.
     *
     * Idempotent in both directions: with nothing to do it performs no request at
     * all, which is what keeps the re-render this triggers from looping.
     *
     * A real gift line (`_free_gift: 'true'`) is never the target and never
     * modified — the marker goes on a line that is STAYING, so it survives the
     * gift being removed.
     */
    setDeclined(on) {
      const root = window.Shopify.routes.root;
      const marker = (it) => String((it.properties || {})._free_gift || '');
      return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => {
          const items = (cart && cart.items) || [];

          // EVERY REMAINING LINE, not just one. Checkout is where a customer edits
          // quantities and removes things, and cart-gift.js does not run there — so
          // a marker on a single line is erased the moment that line goes, and the
          // transform re-grants a gift against an explicit refusal. Marking every
          // eligible line means the refusal survives until the cart is empty, and
          // the transform stands down on a marker found ANYWHERE.
          //
          // A line already marked 'true' is a GIFT, not evidence of a decision, and
          // is skipped in both directions. Treating it as evidence was the bug that
          // made reject() a no-op: the gift line's own 'true' satisfied the
          // "something is already marked" test, so the refusal was never written —
          // on the one path that runs precisely because Shopify refused the gift.
          const targets = on
            ? items.filter((it) => marker(it) === '')
            : items.filter((it) => marker(it) === DECLINED);
          if (!targets.length) return null;

          // Sequential, not Promise.all: each write is a cart mutation, and Shopify
          // serialises them anyway. Line keys are per-line, so changing one does not
          // invalidate another's key — but issuing them in order keeps a failure
          // half-way through easy to reason about (some lines marked, which still
          // suppresses, rather than an unpredictable subset).
          return targets.reduce(
            (chain, it) => chain.then(() => this.writeProperties(it, on ? DECLINED : null)),
            Promise.resolve()
          );
        })
        .catch(() => null); // the offer degrades to today's behaviour; never break the cart
    }

    /** Rewrite one line's properties with `_free_gift` set to `value`, or removed when null. */
    writeProperties(item, value) {
      const properties = {};
      const source = item.properties || {};
      Object.keys(source).forEach((k) => {
        if (k === '_free_gift') return;
        const v = source[k];
        if (v === null || v === undefined || v === '') return;
        properties[k] = String(v);
      });
      if (value !== null) properties._free_gift = value;
      return fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: item.key, properties }),
      })
        .then((r) => r.json())
        .then((parsed) => {
          if (parsed && parsed.status) throw new Error(parsed.description || parsed.message);
          return parsed;
        })
        .catch((error) => {
          // Marking is best-effort. A failure leaves the cart exactly as it was
          // and the offer behaving as it did before this contract existed.
          console.error('[cart-gift] could not record stand-down', error);
          return null;
        });
    }

    /**
     * Write the refusal, bounded, and never reject.
     *
     * Callers must be able to `.then()` this and know two things: it will settle,
     * and it will not throw. Whether the marking actually landed is deliberately
     * not reported — every caller proceeds either way, because the alternative to
     * a missing marker is a stranded cart, which is worse than a gift granted
     * against a refusal.
     */
    markDeclined() {
      return withTimeout(this.setDeclined(true), MARK_TIMEOUT_MS).catch(() => {});
    }

    /**
     * Stand down: hold the cart shut while the refusal is written, then release it
     * and offer the reclaim banner.
     *
     * THE LOCK IS THE POINT. Writing the marker takes a round trip, and every one
     * of these paths used to leave the cart fully interactive across it — so a
     * shopper who pressed Checkout or Shop Pay inside that window reached the
     * Function with no marker on the cart and was handed the gift the module had
     * just decided to withhold, on top of the discount code they were keeping.
     * reject() already had this shape; these four paths did not.
     *
     * The release is in `finally`, so a marking that fails or times out costs the
     * customer nothing: the cart comes back and the offer is still there to claim
     * by hand.
     */
    standDown() {
      this.lockCart();
      return this.markDeclined().finally(() => {
        this.unlockCart();
        this.showReclaim();
      });
    }

    maybeAutoAdd(button) {
      const blocked = store.get(KEY_BLOCKED);
      const qualifying = this.dataset.qualifying || '';

      // A discount code is already earning this customer money and ours cannot
      // combine with it. Adding the gift would quietly cancel their code, and
      // on a large cart a percentage is worth more than the gift -- so this is
      // a trade only they should make. Show the offer; do not take it for them.
      if (this.dataset.otherCode === 'true') {
        this.standDown();
        return;
      }

      // A block is scoped to the cart it was made against, not to the whole
      // session. Standing down forever meant one refusal -- or one transient
      // failure -- left the customer clicking "claim" for the rest of their
      // visit even though the offer was working again. Once they change what
      // else is in the cart, the situation is new and we offer it properly.
      if (blocked) {
        if (blocked.split(':')[1] === qualifying) {
          this.standDown();
          return;
        }
        // The cart changed, so the situation is new and the old stand-down is
        // withdrawn — from the server too, or checkout would keep honouring a
        // refusal this module has already forgiven.
        store.clear(KEY_BLOCKED);
        store.clear(KEY_ADDED);
        this.setDeclined(false);
      }

      if (store.get(KEY_ADDED)) {
        // It was in the cart and now is not, and we did not take it out.
        store.set(KEY_BLOCKED, `removed:${qualifying}`);
        store.clear(KEY_ADDED);
        this.standDown();
        return;
      }

      // Liquid can only see discounts that are currently ALLOCATING money, so a
      // code sitting on the cart below its own minimum -- SAVE200 on a $1,800
      // cart, say -- is invisible to the check above. Auto-adding would evict it
      // silently, and the customer would never learn why their code vanished.
      // /cart.js lists every code held on the cart, applicable or not.
      this.confirmNoOtherCode().then(({ clear, cart }) => {
        if (!clear) {
          this.dataset.otherCode = 'true';
          this.standDown();
          return;
        }
        this.addGift(button, true, cart);
      });
    }

    /** Resolves true when the cart holds no discount code other than ours. */
    confirmNoOtherCode() {
      const mine = (this.dataset.giftCode || '').toUpperCase();
      return fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((cart) => ({
          clear: !(cart.discount_codes || []).some((c) => (c.code || '').toUpperCase() !== mine),
          cart,
        }))
        .catch(() => ({ clear: true, cart: null })); // unreachable cart API: fall back to the Liquid answer
    }

    /**
     * The customer took the gift out but still qualifies for it. Swap the quiet
     * offer card for the reclaim banner — this only ever runs inside the
     * 'unlocked' state, so it cannot appear once the cart drops below the
     * threshold and the offer is no longer real.
     */
    showReclaim() {
      const banner = this.querySelector('[data-gift-reclaim]');
      const standard = this.querySelector('[data-gift-offer]');
      if (!banner) return;
      banner.hidden = false;
      if (standard) standard.hidden = true;
      this.classList.add('cart-gift--reclaim');

      const note = banner.querySelector('[data-gift-code-note]');
      if (note) note.hidden = this.dataset.otherCode !== 'true';

      // Move the banner to where it is actually visible for this screen: the
      // empty space under the line items on desktop, directly under the heading
      // on mobile (where the order summary is a long scroll away). The element
      // is relocated, not duplicated -- a second copy would mean a second module
      // and a second auto-add. Its click handler travels with the element.
      // Empty BOTH slots first. They sit outside the markup the theme re-renders
      // (so a cart update cannot wipe a relocated banner), which also means
      // nothing else ever clears them: appending blindly stacked a new banner on
      // every re-render, left a live "claim" banner above an emptied cart, and
      // gave each stale copy its own click handler and its own busy flag -- two
      // clicks, two adds, quantity 2, and the whole line rejected.
      const slots = ['cart-gift-desktop-slot', 'cart-gift-mobile-slot']
        .map((id) => document.getElementById(id))
        .filter(Boolean);
      slots.forEach((s) => s.replaceChildren());

      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      const slot = document.getElementById(isDesktop ? 'cart-gift-desktop-slot' : 'cart-gift-mobile-slot');
      if (slot) slot.appendChild(banner);
    }

    /** Nothing to offer in this state — make sure no stale banner is left behind. */
    clearSlots() {
      ['cart-gift-desktop-slot', 'cart-gift-mobile-slot'].forEach((id) => {
        const s = document.getElementById(id);
        if (s) s.replaceChildren();
      });
    }

    /** Gift is present but was not discounted to zero — undo it. */
    reject() {
      const lineKey = this.dataset.lineKey;
      if (!lineKey || this.busy) return;
      store.set(KEY_BLOCKED, `refused:${this.dataset.qualifying || ''}`);
      store.clear(KEY_ADDED);
      this.busy = true;
      // Removing a line renumbers the others exactly as adding one does, so this
      // path needs the same lock. It was missing here -- on the one path that
      // runs precisely when something has already gone wrong.
      this.lockCart();
      // Record the refusal BEFORE the gift line goes, and WAIT for it. Shopify
      // would not make this line free, so if the removal landed first there would
      // be a window with neither a gift line nor a refusal on the cart — and an
      // express checkout started in another tab during that window would be handed
      // the very gift Shopify just refused. markDeclined always settles and never
      // rejects, so the removal always follows; at worst it follows an unrecorded
      // refusal, which is exactly the behaviour before this contract existed. The
      // cart is locked throughout, so the extra round trip costs no interactivity —
      // and the bound is what stops a request that never settles from leaving the
      // cart inert with the gift still in it and no way to remove it.
      this.markDeclined().then(() => {
        this.request(`${window.Shopify.routes.root}cart/change.js`, { id: lineKey, quantity: 0 });
      });
    }

    addGift(button, isAuto, cart) {
      if (this.busy) return;
      this.busy = true;
      // Freeze cart controls for the moment the line is going in. See lockCart().
      this.lockCart();

      if (button) {
        button.setAttribute('aria-busy', 'true');
        if (isAuto) button.textContent = 'Adding your gift…';
      }

      let properties = { _free_gift: 'true' };
      try {
        properties = Object.assign(JSON.parse(this.dataset.giftProperties || '{}'), properties);
      } catch (e) {
        console.error('[cart-gift] bad gift properties', e);
      }

      this.addAndDiscount(properties, cart).finally(() => {
        this.busy = false;
      });
    }

    /**
     * Add the line, then apply the gift discount code.
     *
     * The discount is a CODE, not an automatic discount, so nothing makes the
     * gift free unless we apply it. /discount/<code> is Shopify's own entry
     * point for this and sets it on the session; there is no Ajax cart API for
     * applying a code. If this step fails the line simply stays at full price
     * and the 'rejected' path removes it, so a failure is never a silent charge.
     */
    addAndDiscount(properties, cart) {
      const root = window.Shopify.routes.root;
      const code = this.dataset.giftCode;
      const targets = this.sectionTargets();
      const gift = { id: Number(this.dataset.variantId), quantity: 1, properties };

      // Code FIRST, line second. Adding first left a window -- one network round
      // trip -- where the server cart held the gift at full price. Checkout is
      // reachable during that window, so a customer could pay for the thing the
      // page had just called free. Applying the code to a cart that does not yet
      // contain the gift is harmless (it simply sits inapplicable), and means
      // there is no instant where an undiscounted gift line exists.
      //
      // A plain add. Until 2026-09-30 this went through reinsert() to put the
      // gift beneath the other lines, which removed and re-added the ring --
      // and each re-add reached Meta/GA4 as a fake add_to_cart.
      return (code ? fetch(`${root}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }) : Promise.resolve())
        .then(() =>
          this.postCartRetry(
            'cart/add.js',
            { items: [gift], sections: this.sectionNames(targets), sections_url: window.location.pathname },
            3
          )
        )
        .then((parsed) => {
          // Only now, once the line genuinely exists. Marking it beforehand meant
          // a failed add looked identical to the customer deleting the gift, so
          // one bad request downgraded them to clicking "claim" by hand.
          store.set(KEY_ADDED, '1');
          this.render(parsed, targets);
        })
        .catch((error) => {
          console.error('[cart-gift]', error);
          this.reloadOnce();
        });
    }

    fetchCart() {
      return fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
    }

    /** POST JSON to a cart endpoint; a non-2xx or a Shopify error body rejects. */
    postCart(path, body) {
      return fetch(`${window.Shopify.routes.root}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then((r) => r.json())
        .then((parsed) => {
          if (!parsed || parsed.status) throw new Error((parsed && (parsed.description || parsed.message)) || 'cart request failed');
          return parsed;
        });
    }

    /** Same as postCart, retried: a reorder has already removed lines by the time the re-add runs. */
    postCartRetry(path, body, attempts) {
      const delays = [400, 1200];
      const attempt = (n) =>
        this.postCart(path, body).catch((error) => {
          if (n >= attempts - 1) throw error;
          return new Promise((resolve) => setTimeout(resolve, delays[Math.min(n, delays.length - 1)])).then(() => attempt(n + 1));
        });
      return attempt(0);
    }

    /** A /cart.js line as /cart/add.js wants it, every property carried forward. */
    lineToItem(it) {
      const properties = {};
      const source = it.properties || {};
      Object.keys(source).forEach((k) => {
        const v = source[k];
        if (v === null || v === undefined || v === '') return;
        properties[k] = String(v);
      });
      const item = { id: it.variant_id, quantity: it.quantity, properties };
      const plan = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan;
      if (plan && plan.id) item.selling_plan = plan.id;
      return item;
    }

    /**
     * Put the gift line at the BOTTOM of the cart.
     *
     * Shopify lists cart lines newest-first, and checkout prints them in that
     * order. The gift is added AFTER the ring that earned it, so it is the newest
     * line and checkout opened with a pair of free earrings above a $3,000 ring.
     * The cart page hides this with a two-pass render; checkout cannot be
     * templated. Nothing reorders a line in place -- property writes, quantity
     * changes and update.js all keep positions (measured) -- so the only way to
     * move the gift down is to make everything else newer: remove every other
     * line and put it back in ONE add, bottom line first, which leaves the lines
     * exactly as they were with the gift beneath. The mechanics live in
     * reinsert(); this is the "only if something sits beneath the gift" gate.
     *
     * Resolves with the re-add response (sections included, so the caller can
     * render the final cart), or null when nothing needed to move.
     */
    reorderBehindGift(cart, targets) {
      const items = (cart && cart.items) || [];
      const isGift = (it) => String((it.properties || {})._free_gift || '') === 'true';
      const firstGift = items.findIndex(isGift);
      const lastOther = items.reduce((acc, it, i) => (isGift(it) ? acc : i), -1);
      if (firstGift < 0 || lastOther < firstGift) return Promise.resolve(null);
      return this.reinsert(cart, targets, null);
    }

    /**
     * Remove every non-gift line and put it back in ONE add, bottom line first,
     * with `leading` (a gift line to create) at the very front of that add so it
     * lands as the oldest line. With no `leading` this is a pure reorder.
     *
     * The dangerous half is the gap between the removal and the re-add. Three
     * things close it: the cart controls are inert throughout (lockCart), the
     * re-add is retried, and the lines are written to sessionStorage BEFORE they
     * are removed so a load that finds them there puts them back first
     * (restoreLines). What was actually removed is read from the removal
     * response, not assumed: a key that went stale under us (a sibling script
     * rewriting that line's properties) is simply not re-added, and a line
     * Shopify took along with it is. The gift itself is never stashed -- if the
     * add fails the module simply offers it again on the next load.
     *
     * Degrades rather than fails: when the removal is refused and there is a
     * gift to add, the gift is added on its own (on top, as before this existed).
     * Resolves with the add response (sections included), or null when there was
     * nothing to add.
     */
    reinsert(cart, targets, leading) {
      const items = (cart && cart.items) || [];
      const isGift = (it) => String((it.properties || {})._free_gift || '') === 'true';
      const move = items.filter((it) => !isGift(it));
      const add = (list) =>
        this.postCartRetry(
          'cart/add.js',
          { items: list, sections: this.sectionNames(targets), sections_url: window.location.pathname },
          3
        );
      if (!move.length) return leading ? add([leading]) : Promise.resolve(null);

      const removals = {};
      move.forEach((it) => {
        removals[it.key] = 0;
      });
      // Bottom line first: the last item in an add.js batch ends up on top.
      const stash = move
        .slice()
        .reverse()
        .map((it) => ({ key: it.key, item: this.lineToItem(it) }));
      store.set(KEY_REORDER, JSON.stringify(stash.map((s) => s.item)));
      let gone = false; // something has actually left the cart

      return this.postCart('cart/update.js', { updates: removals })
        .then((after) => {
          const left = new Set(((after && after.items) || []).map((it) => it.key));
          const removed = stash.filter((s) => !left.has(s.key)).map((s) => s.item);
          if (!removed.length) {
            store.clear(KEY_REORDER);
            return leading ? add([leading]) : null;
          }
          gone = true;
          store.set(KEY_REORDER, JSON.stringify(removed));
          return add(leading ? [leading].concat(removed) : removed).then((parsed) => {
            store.clear(KEY_REORDER);
            return parsed;
          });
        })
        .catch((error) => {
          if (gone) throw error;
          // Removal failed outright: nothing left the cart, nothing to restore.
          store.clear(KEY_REORDER);
          if (leading) return add([leading]);
          return null;
        });
    }

    /** On a cart that already holds the gift: move it down if something sits beneath it. */
    settleOrder() {
      if (this.busy || reordering) return Promise.resolve();
      reordering = true;
      const targets = this.sectionTargets();
      return this.fetchCart()
        .then((cart) => {
          const items = (cart && cart.items) || [];
          const isGift = (it) => String((it.properties || {})._free_gift || '') === 'true';
          const firstGift = items.findIndex(isGift);
          const lastOther = items.reduce((acc, it, i) => (isGift(it) ? acc : i), -1);
          if (firstGift < 0 || lastOther < firstGift) return null;
          this.busy = true;
          this.lockCart();
          return this.reorderBehindGift(cart, targets).then((reordered) => {
            if (reordered) this.render(reordered, targets);
            else this.unlockCart();
          });
        })
        .catch((error) => {
          console.error('[cart-gift] reorder', error);
          this.reloadOnce();
        })
        .finally(() => {
          this.busy = false;
          reordering = false;
        });
    }

    /**
     * Put back lines a previous reorder removed and failed to re-add.
     *
     * Idempotent: a line already in the cart (same variant, same properties) is
     * skipped, so a stash that was in fact re-added before the page died is a
     * no-op. Resolves true when it re-rendered the cart (the fresh module picks
     * up from there), false when the state machine should continue as normal.
     * A failure keeps the stash for the next load rather than reloading, since
     * a reload here could only loop.
     */
    restoreLines(pending) {
      let stash = [];
      try {
        stash = JSON.parse(pending) || [];
      } catch (e) {
        stash = [];
      }
      if (!stash.length) {
        store.clear(KEY_REORDER);
        return Promise.resolve(false);
      }
      const targets = this.sectionTargets();
      const same = (a, b) => JSON.stringify(a || {}) === JSON.stringify(b || {});
      this.lockCart();
      return this.fetchCart()
        .then((cart) => {
          const items = (cart && cart.items) || [];
          const missing = stash.filter(
            (s) => !items.some((it) => it.variant_id === s.id && same(this.lineToItem(it).properties, s.properties))
          );
          if (!missing.length) {
            store.clear(KEY_REORDER);
            return null;
          }
          return this.postCartRetry(
            'cart/add.js',
            { items: missing, sections: this.sectionNames(targets), sections_url: window.location.pathname },
            3
          );
        })
        .then((parsed) => {
          if (!parsed) {
            this.unlockCart();
            return false;
          }
          store.clear(KEY_REORDER);
          this.render(parsed, targets);
          return true;
        })
        .catch((error) => {
          console.error('[cart-gift] restore', error);
          this.unlockCart();
          return false;
        });
    }

    /**
     * Disable the cart's own controls while we are inserting the gift line.
     *
     * The cart's remove and quantity buttons address lines by POSITION, not by
     * line key. Adding the gift renumbers every line beneath it, so a customer
     * who clicks "remove" during that window sends the index they could see and
     * Shopify deletes whatever now sits there — observed removing the gift when
     * the customer meant to remove a ring. Blocking clicks for the round trip
     * closes that window instead of narrowing it.
     */
    lockCart() {
      document.documentElement.classList.add('cart-gift-busy');
      // pointer-events only stops a mouse. A keyboard or switch-control user can
      // still Tab to a remove button or checkout and press Enter mid-flight, so
      // the controls have to be genuinely inert, not just unclickable.
      lockTargets().forEach((el) => el.setAttribute('inert', ''));
    }

    unlockCart() {
      document.documentElement.classList.remove('cart-gift-busy');
      lockTargets().forEach((el) => el.removeAttribute('inert'));
      // The optimistic gift row is a first-paint promise that the add is about
      // to land. Reaching unlock while it is still in the DOM means the add did
      // NOT land (stand-down, failure) -- withdraw it rather than leave a row
      // the server cart does not contain. On success the section patch has
      // already replaced it with the real line, so this is a no-op there.
      document.querySelectorAll('[data-gift-optimistic]').forEach((el) => el.remove());
    }

    /**
     * Section names to request alongside a cart mutation. In the drawer the
     * renderer is the drawer itself, and it looks sections up by ITS OWN list
     * ('cart-drawer', 'cart-icon-bubble') -- requesting anything else hands
     * renderContents a sections object it cannot read, and that failure
     * surfaces as a full page reload the customer has to sit through.
     */
    sectionNames(targets) {
      const drawer = document.querySelector('cart-drawer');
      if (this.dataset.context !== 'page' && drawer && typeof drawer.getSectionsToRender === 'function') {
        return drawer.getSectionsToRender().map((s) => s.id);
      }
      return targets.map((t) => t.section);
    }

    /** Sections to refresh, resolved from the DOM so ids stay in one place. */
    sectionTargets() {
      const targets = [];
      ['main-cart-items', 'main-cart-footer'].forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.dataset.id) targets.push({ section: el.dataset.id, el, selector: '.js-contents' });
      });
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) targets.push({ section: 'cart-icon-bubble', el: bubble, selector: '.shopify-section' });
      return targets;
    }

    request(url, payload) {
      const targets = this.sectionTargets();
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          ...payload,
          sections: this.sectionNames(targets),
          sections_url: window.location.pathname,
        }),
      })
        .then((response) => response.json())
        .then((parsed) => {
          if (parsed.status) throw new Error(parsed.description || parsed.message);
          this.render(parsed, targets);
        })
        .catch((error) => {
          console.error('[cart-gift]', error);
          // Never leave the page showing a cart the server disagrees with.
          this.reloadOnce();
        })
        .finally(() => {
          this.busy = false;
        });
    }


    /**
     * Reload after a failure, but only once.
     *
     * Reloading was the recovery for a failed add or remove -- and the reloaded
     * page re-enters the same state and retries the same failing action, one
     * cart mutation per lap. A persistently failing discount code turned the
     * cart page into an add/remove machine. The marker lives in the URL rather
     * than sessionStorage because the loop is worst exactly where storage is
     * unavailable.
     */
    reloadOnce() {
      const url = new URL(window.location.href);
      if (url.searchParams.get('gift_retry')) {
        this.unlockCart();
        return;
      }
      url.searchParams.set('gift_retry', '1');
      window.location.replace(url.toString());
    }

    /**
     * Patch the page in place from the HTML that rode in on the mutation
     * response; reload only when that HTML is missing or unusable.
     *
     * An earlier build reloaded the whole cart page here. The reason was a real
     * race: sections fetched in a SEPARATE request could be rendered from the
     * pre-add cart, so totals and rows disagreed on screen. Sections embedded
     * in the mutation response cannot be stale -- Shopify renders them from the
     * cart the mutation produced -- and the customer's own cart controls are
     * inert for the whole window, so there is no second writer. The gift module
     * itself lives inside the footer's .js-contents, so the patch also swaps in
     * its fresh state, and that element's connectedCallback sweeps any
     * relocated banner out of the slots.
     */
    render(parsed, targets) {
      const drawer = document.querySelector('cart-drawer');
      if (this.dataset.context !== 'page' && drawer && typeof drawer.renderContents === 'function') {
        this.unlockCart();
        drawer.renderContents(parsed);
        return;
      }

      const sections = parsed && parsed.sections;
      if (sections) {
        const patched = targets.every((t) => {
          const html = sections[t.section];
          if (!html) return false;
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(t.selector);
          if (!fresh) return false;
          const dest = t.el.querySelector(t.selector) || t.el;
          dest.innerHTML = fresh.innerHTML;
          return true;
        });
        if (patched) {
          this.unlockCart();
          const done = new URL(window.location.href);
          if (done.searchParams.get('gift_retry')) {
            done.searchParams.delete('gift_retry');
            window.history.replaceState({}, '', done.pathname + done.search + done.hash);
          }
          return;
        }
      }

      // Recovery: never leave the page showing a cart the server disagrees with.
      const done = new URL(window.location.href);
      done.searchParams.delete('gift_retry');
      window.location.replace(done.pathname + done.search);
    }
  }

  if (!customElements.get('cart-gift-unlock')) {
    customElements.define('cart-gift-unlock', CartGiftUnlock);
  }

  /**
   * window.BCGift -- put the gift in JUST BEFORE a qualifying ring is added.
   *
   * Shopify lists cart lines newest-first and checkout prints them in that
   * order. Added by the cart page AFTER the ring, the studs sat above a $3,000
   * ring at checkout; the only fix that ever worked removed and re-added the
   * ring, and every re-add reached Meta/GA4/Klaviyo as a fake add to cart. So
   * the order is decided at the one moment it costs nothing: the gift goes in
   * first, then the ring's own add runs unchanged and lands on top.
   *
   * TWO REQUESTS, NEVER ONE. Sending gift + ring in a single add.js would change
   * the response the product form and the Klaviyo "Added to Cart" wrapper in
   * layout/theme.liquid read (a single add returns the item; a batch does not).
   *
   * CONSERVATIVE BY DESIGN. Any doubt -- no expected price, a competing code,
   * an earlier refusal, an unreadable cart -- and this does nothing: the cart
   * page's module still adds the gift afterwards, on top, which is only
   * cosmetic. A wrong guess here must never grant a gift the cart page would
   * not grant, so every check below is the cart module's own rule or stricter,
   * and the ring price used is its `_expected_price`, a floor the pricing
   * function never charges below.
   *
   * Usage (product-form.js, bc-diamond-filters.js):
   *   const gift = BCGift.beforeAdd({ expectedCents, quantity, declined });
   *   gift.wait.then(() => ringAdd()).then(ok => gift.done(ok));
   * `wait` always resolves, within WAIT_MS at most -- the ring add is never held
   * longer than that and never blocked by a gift failure. `done(false)` removes
   * the gift again if it landed, so a studs-only cart never remains.
   */
  const WAIT_MS = 3000;
  const isGiftLine = (it) => String((it.properties || {})._free_gift || '') === 'true';
  const hasMarker = (it) => String((it.properties || {})._free_gift || '') !== '';

  function readConfig() {
    try {
      const el = document.getElementById('bc-gift-config');
      if (!el) return null;
      const cfg = JSON.parse(el.textContent);
      if (!cfg || !cfg.variantId || !cfg.code || !(cfg.thresholdCents > 0) || !cfg.currency) return null;
      return cfg;
    } catch (e) {
      return null;
    }
  }

  /**
   * A discount code other than ours held in the `discount_code` cookie. Same
   * rule as competingDiscountCode() in bc-tracking-async.js: EVERY token is
   * read (duplicates at different paths are indistinguishable), and any one
   * that is not our code counts. /cart.js cannot see a code held for an empty
   * cart, which is exactly the cart a first ring goes into.
   */
  function cookieCode(mine) {
    let raw = '';
    try {
      raw = document.cookie || '';
    } catch (e) {
      return 'unreadable';
    }
    const tokens = raw.split(';');
    for (let i = 0; i < tokens.length; i++) {
      const eq = tokens[i].indexOf('=');
      if (eq === -1 || tokens[i].slice(0, eq).trim() !== 'discount_code') continue;
      let value = tokens[i].slice(eq + 1);
      try {
        value = decodeURIComponent(value);
      } catch (e) {
        /* raw text is the more cautious answer */
      }
      value = value.trim();
      if (value && value.toUpperCase() !== mine) return value;
    }
    return '';
  }

  /** Resolves with the cart when a gift may be added ahead of this ring, else null. */
  function eligibleCart(cfg, ringCents) {
    const mine = String(cfg.code).toUpperCase();
    if (cfg.available === false) return Promise.resolve(null);
    if (!(ringCents > 0)) return Promise.resolve(null);
    const active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (active && String(active).toUpperCase() !== String(cfg.currency).toUpperCase()) return Promise.resolve(null);
    if (cookieCode(mine)) return Promise.resolve(null);
    // The customer said no (removed it) or Shopify said no (refused it). Either
    // way the cart page owns the next step; a pending reorder stash likewise.
    if (store.get(KEY_BLOCKED) || store.get(KEY_REORDER)) return Promise.resolve(null);

    return fetch(`${window.Shopify.routes.root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((cart) => {
        if (!cart || !Array.isArray(cart.items)) return null;
        if (String(cart.currency || '').toUpperCase() !== String(cfg.currency).toUpperCase()) return null;
        // Already has the gift, or carries a refusal ('declined') anywhere.
        if (cart.items.some(hasMarker)) return null;
        // Any code on the cart other than ours, applicable or not (cart module's rule).
        if ((cart.discount_codes || []).some((c) => String((c && c.code) || '').toUpperCase() !== mine)) return null;
        const others = cart.items.filter((it) => !isGiftLine(it));
        // bcGiftAdded with no gift in a cart that still has lines = the customer
        // took it out; the cart page turns that into a refusal. On an empty cart
        // the marker is stale (nothing left to have removed it from).
        if (store.get(KEY_ADDED)) {
          if (others.length) return null;
          store.clear(KEY_ADDED);
        }
        const qualifying = others.reduce((sum, it) => sum + (Number(it.original_line_price) || 0), 0) + ringCents;
        return qualifying >= cfg.thresholdCents ? cart : null;
      })
      .catch(() => null);
  }

  function beforeAdd(opts) {
    const noop = { wait: Promise.resolve(), done() {} };
    try {
      const cfg = readConfig();
      if (!cfg || !opts) return noop;
      if (opts.declined) return noop; // the form carries a stand-down marker
      const quantity = Math.max(1, parseInt(opts.quantity, 10) || 1);
      const ringCents = (parseInt(opts.expectedCents, 10) || 0) * quantity;
      const root = window.Shopify.routes.root;
      const mine = String(cfg.code).toUpperCase();

      let giftKey = null;
      let ringDone = null; // true / false once the ring add has settled
      let timedOut = false; // the ring add was released before the gift chain finished
      let properties = { _free_gift: 'true' };
      if (cfg.properties && typeof cfg.properties === 'object') properties = Object.assign({}, cfg.properties, properties);

      const removeGift = () =>
        fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: giftKey, quantity: 0 }),
        })
          .catch((e) => console.error('[cart-gift] could not undo gift', e))
          .finally(() => {
            giftKey = null;
            store.clear(KEY_ADDED);
          });

      const addGiftLine = () =>
        fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(cfg.variantId), quantity: 1, properties }] }),
        })
          .then((r) => r.json())
          .then((parsed) => {
            const line = parsed && !parsed.status && parsed.items && parsed.items[0];
            if (!line || !line.key) return null;
            giftKey = line.key;
            store.set(KEY_ADDED, '1');
            store.clear(KEY_BLOCKED);
            // The ring failed while the gift was in flight: take it back out.
            if (ringDone === false) return removeGift().then(() => null);
            return giftKey;
          });

      const chain = eligibleCart(cfg, ringCents)
        .then((cart) => {
          if (!cart) return null;
          // Code FIRST, line second -- same order as addAndDiscount, so there is
          // never an instant with an undiscounted gift line on the cart.
          //
          // Through the Ajax API's `discount` field, not /discount/<code>: one
          // request instead of a redirect pair (Shopify throttles a session's
          // cart endpoints at a few requests a second, and the add click
          // already fires tracking writes), it works on an empty cart (the code
          // is held, "not applicable" until the ring lands -- measured
          // 2026-09-30), and the answer IS the cart, so the gift goes in only
          // when that cart really holds our code. Without that check a
          // throttled apply added a full-price gift, which the cart page then
          // refused and recorded as declined: the customer lost the offer.
          // Setting `discount` replaces the cart's codes; eligibleCart has
          // already required that there are none but ours.
          return fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: cfg.code }),
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((after) => {
              const held = after && (after.discount_codes || []).some((c) => String((c && c.code) || '').toUpperCase() === mine);
              if (!held) return null;
              // Too late: the ring add has already been released (timeout) or has
              // answered. Adding now would land the gift on top anyway and could
              // race the cart page's own add, so leave it to the cart page.
              if (timedOut || ringDone !== null) return null;
              return addGiftLine();
            });
        })
        .catch((e) => {
          console.error('[cart-gift] gift before ring', e);
          return null;
        });

      return {
        wait: withTimeout(chain, WAIT_MS)
          .catch(() => {
            timedOut = true;
          })
          .then(() => undefined),
        done(ok) {
          // First answer wins: a throw later in the caller's success path must
          // not be read as the ring failing and pull a gift the ring earned.
          if (ringDone !== null) return;
          ringDone = !!ok;
          if (ok) return;
          chain.then(() => (giftKey ? removeGift() : null));
        },
      };
    } catch (e) {
      console.error('[cart-gift] beforeAdd', e);
      return noop;
    }
  }

  window.BCGift = { beforeAdd, readConfig };
})();
